// pnlExporters.js — iter180m
// Shared Excel + PDF export utilities for P&L statements.
//
// CRITICAL: these exporters consume the SAME outlet block objects that
// the UI renders. They walk the same section order, the same line items,
// the same labels. They never recompute, never fetch, never aggregate.
// Architecture: MTD engine → Finalized snapshot → Review & Compare → Export
// All four stages read from the same source of truth.
//
// Input shape (one outlet block as returned by /api/admin/pnl,
// /admin/pnl/period/*, or /admin/pnl/compare):
//   { outlet_name, total_revenue, food_sale, beverage_sale, room_revenue,
//     food_cost, beverage_cost, food_cost_pct, beverage_cost_pct,
//     other_purchases, maintenance_cost, marketing_spend_mtd,
//     variable_costs, prorated_fixed_by_category, custom_fixed_fields,
//     prorated_fixed, gross_profit, gross_margin_pct,
//     net_profit, net_margin_pct,
//     budget_mtd, gp_budget, np_budget, sales_variance, ...
//     rooms_enabled, _review_source, finalized, ... }

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Section model — must match PnLStatementBody + OutletPnlCard order ──
//
// Each entry describes ONE line in the statement, with:
//   field    : key on the outlet block (numeric)
//   indent   : visual indent (0 = section total, 1 = line item)
//   isSection: true → render as section header row
//   isTotal  : true → render as bold row
//   ratio    : optional [numerator_field, denominator_field] for % display
//   listSrc  : optional name of dict/list field whose entries become rows

const STATEMENT_MODEL = [
  { kind: 'section', label: 'Revenue' },
  { kind: 'line',    label: 'Food Net Revenue',     field: 'food_sale' },
  { kind: 'line',    label: 'Beverage Net Revenue', field: 'beverage_sale' },
  { kind: 'roomline',label: 'Room Revenue',         field: 'room_revenue' },
  { kind: 'total',   label: 'Total Revenue',        field: 'total_revenue' },

  { kind: 'section', label: 'Cost of Goods Sold' },
  { kind: 'line',    label: 'Food Cost',            field: 'food_cost',     pctField: 'food_cost_pct' },
  { kind: 'line',    label: 'Beverage Cost',        field: 'beverage_cost', pctField: 'beverage_cost_pct' },
  { kind: 'derived', label: 'Total COGS',           formula: (b) => (b.food_cost || 0) + (b.beverage_cost || 0) },

  { kind: 'total_color', label: 'Gross Profit', field: 'gross_profit', pctField: 'gross_margin_pct', color: 'var(--color-primary)' },

  { kind: 'section', label: 'Operating Expenses' },
  { kind: 'line',    label: 'Other Purchases',  field: 'other_purchases' },
  { kind: 'line',    label: 'Maintenance',      field: 'maintenance_cost' },
  { kind: 'line',    label: 'Marketing Spend',  field: 'marketing_spend_mtd' },
  { kind: 'total',   label: 'Total Variable',   field: 'variable_costs' },

  { kind: 'section', label: 'Fixed Costs' },
  // expanded dynamically: prorated_fixed_by_category + custom_fixed_fields
  { kind: 'fixedExpand' },
  { kind: 'total',   label: 'Total Fixed (MTD)', field: 'prorated_fixed' },

  { kind: 'derived', label: 'Total Expenditure',
    formula: (b) => (b.variable_costs || 0) + (b.prorated_fixed || 0) },

  { kind: 'total_color', label: 'Net Profit / (Loss)', field: 'net_profit', pctField: 'net_margin_pct',
    colorIfNeg: '#A33A3A', color: 'var(--color-primary)' },
];

const inr = (v) =>
  v === null || v === undefined || (typeof v === 'number' && isNaN(v))
    ? '—'
    : '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const pct = (v) =>
  v === null || v === undefined ? '—' : Number(v).toFixed(1) + '%';

function lookupFixedItems(block) {
  /** Return [{label, value}] for the Fixed Costs expand area. */
  const out = [];
  const pf = block?.prorated_fixed_by_category;
  if (pf && typeof pf === 'object' && !Array.isArray(pf)) {
    for (const [k, v] of Object.entries(pf)) {
      out.push({ label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), value: v });
    }
  }
  const cf = block?.custom_fixed_fields;
  if (Array.isArray(cf)) {
    for (const item of cf) {
      out.push({ label: item.label || item.key || 'Custom', value: item.value ?? item.amount });
    }
  } else if (cf && typeof cf === 'object') {
    for (const [k, v] of Object.entries(cf)) out.push({ label: k, value: v });
  }
  return out;
}

function unionFixedLabels(blocks) {
  const seen = new Set();
  const out = [];
  for (const b of blocks) {
    for (const it of lookupFixedItems(b)) {
      if (!seen.has(it.label)) { seen.add(it.label); out.push(it.label); }
    }
  }
  return out;
}

function findFixedValue(block, label) {
  for (const it of lookupFixedItems(block)) {
    if (it.label === label) return it.value;
  }
  return undefined;
}

/** Build the table rows for one or many blocks (multi-column compare).
 *  Each row: { label, indent, isSection, isTotal, color, values: [v1, v2, ...], ratios: [r1, r2] }
 *
 *  blocks = [{ block, label }] (one per column).
 */
export function buildStatementRows(columns) {
  const blocks = columns.map((c) => c.block);
  const showRooms = blocks.some((b) => b?.rooms_enabled);
  const showBudget = blocks.some((b) => b?.budget_mtd != null && b.budget_mtd !== 0);
  const rows = [];

  for (const m of STATEMENT_MODEL) {
    if (m.kind === 'section') {
      rows.push({ label: m.label, isSection: true });
      continue;
    }
    if (m.kind === 'roomline' && !showRooms) continue;
    if (m.kind === 'fixedExpand') {
      const fixedLabels = unionFixedLabels(blocks);
      for (const lbl of fixedLabels) {
        rows.push({
          label: lbl, indent: 1,
          values: blocks.map((b) => findFixedValue(b, lbl)),
        });
      }
      continue;
    }
    const values = blocks.map((b) => {
      if (!b) return undefined;
      if (m.kind === 'derived') return m.formula(b);
      return b[m.field];
    });
    const ratios = m.pctField ? blocks.map((b) => b?.[m.pctField]) : null;
    rows.push({
      label: m.label,
      indent: m.kind === 'line' ? 1 : 0,
      isTotal: m.kind === 'total' || m.kind === 'total_color' || m.kind === 'derived',
      color: m.color,
      colorIfNeg: m.colorIfNeg,
      values,
      ratios,
    });
  }

  // Budget context strip — only if any column has budget_mtd
  if (showBudget) {
    rows.push({ label: 'Budget Reference', isSection: true });
    rows.push({ label: 'Revenue Budget', indent: 1, values: blocks.map((b) => b?.budget_mtd) });
    rows.push({ label: 'GP Budget',      indent: 1, values: blocks.map((b) => b?.gp_budget) });
    rows.push({ label: 'NP Budget',      indent: 1, values: blocks.map((b) => b?.np_budget) });
    rows.push({ label: 'Sales Variance', indent: 1, values: blocks.map((b) => b?.sales_variance) });
  }

  return rows;
}

// ──────────────────────────────────────────────────────────────────────
// EXCEL EXPORT
// ──────────────────────────────────────────────────────────────────────

export function exportStatementXlsx({
  fileName = 'PnL_Statement.xlsx',
  sheetName = 'P&L',
  title = 'Profit & Loss Statement',
  subtitle = '',
  columns,           // [{ label, block }]
  showDeltaColumn = false,
  deltaIndex = null, // { aIdx, bIdx } for delta = B − A
}) {
  const rows = buildStatementRows(columns);

  // Header rows
  const sheetData = [
    [title],
    [subtitle],
    [],
  ];
  const headerRow = ['Line Item', ...columns.map((c) => c.label)];
  if (showDeltaColumn) {
    headerRow.push('Δ (B − A)');
    headerRow.push('Δ %');
  }
  sheetData.push(headerRow);

  // Body
  for (const r of rows) {
    if (r.isSection) {
      sheetData.push([r.label, ...columns.map(() => ''), ...(showDeltaColumn ? ['', ''] : [])]);
      continue;
    }
    const lineLabel = (r.indent ? '  ' : '') + r.label;
    const valueCells = (r.values || []).map((v) =>
      v === null || v === undefined || (typeof v === 'number' && isNaN(v)) ? '' : Number(v),
    );
    const ratioCells = r.ratios
      ? r.ratios.map((p) => (p == null ? '' : `(${Number(p).toFixed(1)}%)`))
      : null;
    // Append ratio in the same cell as value for clarity
    const decoratedValues = valueCells.map((v, i) =>
      ratioCells ? (v === '' ? '' : v) : v,
    );
    const row = [lineLabel, ...decoratedValues];
    if (showDeltaColumn && deltaIndex) {
      const a = r.values?.[deltaIndex.aIdx];
      const b = r.values?.[deltaIndex.bIdx];
      const abs = (a == null || b == null) ? '' : Number(b) - Number(a);
      const pp = (a == null || b == null || a === 0) ? '' : ((Number(b) - Number(a)) / Number(a) * 100);
      row.push(abs);
      row.push(pp === '' ? '' : Number(pp.toFixed(2)));
    }
    sheetData.push(row);

    // If a ratio exists for this row, push a small follow-up row showing it
    if (ratioCells && ratioCells.some((rr) => rr)) {
      const ratioRow = ['    ' + (r.label === 'Food Cost' ? '(% of food revenue)'
                              : r.label === 'Beverage Cost' ? '(% of beverage revenue)'
                              : r.label === 'Gross Profit' ? '(GP %)'
                              : r.label === 'Net Profit / (Loss)' ? '(NP %)'
                              : '(%)'),
                        ...ratioCells];
      if (showDeltaColumn) { ratioRow.push(''); ratioRow.push(''); }
      sheetData.push(ratioRow);
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  // Column widths
  ws['!cols'] = [
    { wch: 36 },
    ...columns.map(() => ({ wch: 18 })),
    ...(showDeltaColumn ? [{ wch: 16 }, { wch: 10 }] : []),
  ];

  // Numeric format on value columns
  const numericFormat = '#,##0;[Red]-#,##0;""';
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let R = 4; R <= range.e.r; ++R) {
    for (let C = 1; C <= range.e.c; ++C) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (!cell) continue;
      if (typeof cell.v === 'number') {
        cell.z = (showDeltaColumn && C === range.e.c) ? '0.00"%"' : numericFormat;
      }
    }
  }

  // Title styling: merge across columns
  const lastCol = headerRow.length - 1;
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
}

// ──────────────────────────────────────────────────────────────────────
// PDF EXPORT — executive-report quality
// ──────────────────────────────────────────────────────────────────────

const PDF_COLORS = {
  primary: [20, 61, 48],          // forest green
  accent: [217, 155, 91],          // muted gold
  text: [26, 36, 33],
  muted: [92, 107, 100],
  faint: [226, 232, 223],
  sectionBg: [237, 241, 238],
  positive: [20, 61, 48],
  negative: [163, 58, 58],
  white: [255, 255, 255],
};

export function exportStatementPdf({
  fileName = 'PnL_Statement.pdf',
  title = 'Profit & Loss Statement',
  subtitle = '',
  brand = 'RestroPulse',
  preparedBy = '',
  columns,
  showDeltaColumn = false,
  deltaIndex = null,
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

  // jsPDF ships with Helvetica/Times/Courier only. We use Helvetica as a
  // close stand-in for Montserrat (geometric sans). The output retains
  // the clean, executive look the spec calls for: subtle separators,
  // strong section headers, no markers or icons.
  doc.setFont('helvetica', 'normal');

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;

  // ── Header band ──
  doc.setFillColor(...PDF_COLORS.primary);
  doc.rect(0, 0, pageW, 56, 'F');
  doc.setTextColor(...PDF_COLORS.white);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(brand, margin, 30);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Financial Reporting · Hospitality Operations', margin, 44);

  // ── Title block ──
  doc.setTextColor(...PDF_COLORS.text);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, 90);
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...PDF_COLORS.muted);
    doc.text(subtitle, margin, 106);
  }

  // ── Column labels strip ──
  doc.setFontSize(8);
  doc.setTextColor(...PDF_COLORS.muted);
  let y = 130;
  // iter180p5 — lock "generated at" stamp to IST so the file says the right
  // thing regardless of the browser's local timezone (e.g. a foreign auditor
  // downloading a P&L PDF still sees IST).
  const _genStamp = new Date().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short',
  }) + ' IST';
  const tag = preparedBy ? `Prepared by ${preparedBy}  ·  ${_genStamp}` : _genStamp;
  doc.text(tag, margin, y);
  y += 8;

  // ── Build the statement table ──
  const rows = buildStatementRows(columns);
  const head = [['Line Item', ...columns.map((c) => c.label).map(stripBadge), ...(showDeltaColumn ? ['Δ (B − A)', 'Δ %'] : [])]];
  const body = [];
  for (const r of rows) {
    if (r.isSection) {
      body.push([{
        content: r.label,
        colSpan: head[0].length,
        styles: {
          fillColor: PDF_COLORS.sectionBg,
          textColor: PDF_COLORS.primary,
          fontStyle: 'bold',
          fontSize: 8.5,
          cellPadding: { top: 5, right: 6, bottom: 5, left: 6 },
        },
      }]);
      continue;
    }
    const labelCell = {
      content: (r.indent ? '    ' : '') + r.label,
      styles: { fontStyle: r.isTotal ? 'bold' : 'normal' },
    };
    const valueCells = (r.values || []).map((v, i) => {
      let display = inr(v);
      if (r.ratios && r.ratios[i] != null) {
        display = `${display}\n${pct(r.ratios[i])}`;
      }
      return {
        content: display,
        styles: {
          halign: 'right',
          fontStyle: r.isTotal ? 'bold' : 'normal',
          textColor: r.color ? hexToRgb(r.color) : PDF_COLORS.text,
        },
      };
    });
    let deltaCells = [];
    if (showDeltaColumn && deltaIndex) {
      const a = r.values?.[deltaIndex.aIdx];
      const b = r.values?.[deltaIndex.bIdx];
      const abs = (a == null || b == null) ? null : Number(b) - Number(a);
      const pp = (a == null || b == null || a === 0) ? null : ((Number(b) - Number(a)) / Number(a) * 100);
      const dColor = abs == null ? PDF_COLORS.muted : abs >= 0 ? PDF_COLORS.positive : PDF_COLORS.negative;
      deltaCells = [
        { content: abs == null ? '—' : inr(abs), styles: { halign: 'right', textColor: dColor } },
        { content: pp == null ? '—' : `${pp >= 0 ? '+' : ''}${pp.toFixed(1)}%`, styles: { halign: 'right', textColor: dColor, fontSize: 8 } },
      ];
    }
    body.push([labelCell, ...valueCells, ...deltaCells]);
  }

  autoTable(doc, {
    head,
    body,
    startY: y + 8,
    margin: { left: margin, right: margin },
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
      lineColor: PDF_COLORS.faint,
      lineWidth: 0,
      textColor: PDF_COLORS.text,
    },
    headStyles: {
      fillColor: PDF_COLORS.white,
      textColor: PDF_COLORS.muted,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
      lineWidth: { bottom: 1 },
      lineColor: PDF_COLORS.primary,
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
    },
    didDrawCell: (data) => {
      // Subtle underline under each line item row for the hospitality look.
      // iter180p3 — guard with isFinite() on every coordinate before calling
      // jsPDF.line; autoTable can fire this hook for cells where one of
      // {x, y, height, table.width} is undefined for colSpan rows, which
      // makes jsPDF throw "Invalid arguments passed to jsPDF.line".
      if (data.section !== 'body' || data.column.index !== 0) return;
      const raw0 = data.row && data.row.raw && data.row.raw[0];
      const isSectionRow = raw0 && typeof raw0 === 'object' && raw0.colSpan;
      if (isSectionRow) return;
      const x1 = data.cell && data.cell.x;
      const y  = data.cell && (data.cell.y + (data.cell.height || 0));
      const tw = data.table && data.table.width;
      if (![x1, y, tw].every((v) => typeof v === 'number' && isFinite(v))) return;
      doc.setDrawColor(...PDF_COLORS.faint);
      doc.setLineWidth(0.5);
      doc.line(x1, y, x1 + tw, y);
    },
  });

  // ── Footer band on every page ──
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF_COLORS.muted);
    doc.text('Confidential · Internal management reporting', margin, pageH - 18);
    doc.text(`Page ${i} of ${pages}`, pageW - margin, pageH - 18, { align: 'right' });
    if ([margin, pageH, pageW].every((v) => typeof v === 'number' && isFinite(v))) {
      doc.setDrawColor(...PDF_COLORS.faint);
      doc.line(margin, pageH - 24, pageW - margin, pageH - 24);
    }
  }

  doc.save(fileName);
}

// Strip "· Live / · Finalized / · Aggregated" badges from PDF column heads
// for a cleaner executive look (still implicit from the picker context).
function stripBadge(s) {
  return String(s || '').replace(/\s*·\s*(Live|Finalized|Aggregated|Budget)\b.*$/i, '');
}

function hexToRgb(hex) {
  const m = hex.replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return PDF_COLORS.text;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
